import { createFileRoute, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { parseBenchmarkOptionKey } from '@vizit/organisms';
import type { BreadcrumbItem } from '@vizit/utils';
import * as React from 'react';
import { resolveLoaderWorkspaceId, useActiveWorkspaceId } from '../../../../../hooks/use-workspace-link';
import { ExploreTrendsPage, PagesProvider, parseCompareIds } from '../../../../../pages';
import { beforeLoadAuthenticatedValidation, guardExploreTrends } from '../../../../../router/route-utils';
import { buildCollectionChildBreadcrumbs, getCollectionHref } from '../../../../../router/route-utils';
import { loadCollectionName } from '../../../../../router/route-utils';
import { navigateBackToCollection, navigateWithState } from '../../../../../router/route-utils';
import { useCollectionNamedBreadcrumbs } from '../../../../../router/route-utils';
import { BreadcrumbsPageTemplate } from '../../../../../templates';

export const Route = createFileRoute('/_authenticated/w/$workspaceSlug/library/collections/$collectionId_/trends')({
  staticData: {
    pageName: 'Explore visual trends',
  },
  beforeLoad: async ({ context, location, params }) => {
    await beforeLoadAuthenticatedValidation(context, location);
    await guardExploreTrends(context, params.workspaceSlug, params.collectionId);
  },
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): { image?: string; benchmark?: string } => {
    const [image] = parseCompareIds(search.image);
    return { image, benchmark: parseBenchmarkOptionKey(search.benchmark) };
  },
  loader: async ({ location, params, context }) => {
    const collectionName = await loadCollectionName(context.queryClient, {
      workspaceId: resolveLoaderWorkspaceId(context, params.workspaceSlug),
      collectionId: params.collectionId,
      page: 'explore visual trends',
    });
    return {
      breadcrumbs: buildCollectionChildBreadcrumbs(
        location.state?.from,
        { workspaceSlug: params.workspaceSlug, collectionId: params.collectionId, collectionName },
        'Explore visual trends',
        location.search,
      ),
    };
  },
});

function RouteComponent() {
  const { workspaceSlug, collectionId } = useParams({ from: Route.id });
  const router = useRouter();
  const search = useSearch({ from: Route.id });
  const workspaceId = useActiveWorkspaceId() ?? undefined;
  const { breadcrumbs: loaderBreadcrumbs }: { breadcrumbs: BreadcrumbItem[] } = Route.useLoaderData();
  const breadcrumbs = useCollectionNamedBreadcrumbs(loaderBreadcrumbs, { workspaceSlug, collectionId, workspaceId });

  const collectionHref = getCollectionHref(workspaceSlug, collectionId);

  const handleBackToCollection = React.useCallback(() => {
    navigateBackToCollection(router, { workspaceSlug, collectionId, benchmark: search.benchmark });
  }, [router, workspaceSlug, collectionId, search.benchmark]);

  const handlePickedBenchmarkKeyChange = React.useCallback(
    (key: string | null) => {
      router.navigate({
        to: Route.fullPath,
        params: { workspaceSlug, collectionId },
        search: (prev: Record<string, unknown>) => ({ ...prev, benchmark: key ?? undefined }),
        replace: true,
        state: router.state.location.state,
      });
    },
    [router, workspaceSlug, collectionId],
  );

  const handleOpenAsset = (assetId: string, awaitingCategoryBenchmarkKey: string | null) => {
    const shown = breadcrumbs ?? loaderBreadcrumbs;
    navigateWithState(router, {
      to: '/w/$workspaceSlug/library/assets/$assetId',
      params: { workspaceSlug, assetId },
      search: {
        origin: 'collection',
        collection: collectionId,
        ...(awaitingCategoryBenchmarkKey ? { benchmark: awaitingCategoryBenchmarkKey } : {}),
      },
      from: {
        trail: [
          ...shown.slice(0, -1).filter((crumb) => crumb.href !== undefined),
          { label: 'Explore visual trends', href: `${collectionHref}/trends`, search: router.state.location.search },
        ],
      },
    });
  };

  return (
    <PagesProvider
      template={BreadcrumbsPageTemplate}
      templateProps={{ breadcrumbActionsPosition: 'inline', breadcrumbs }}
    >
      <ExploreTrendsPage
        collectionId={collectionId}
        workspaceId={workspaceId}
        imageId={search.image ?? null}
        onBackToCollection={handleBackToCollection}
        onOpenAsset={handleOpenAsset}
        pickedBenchmarkKey={search.benchmark ?? null}
        onPickedBenchmarkKeyChange={handlePickedBenchmarkKeyChange}
      />
    </PagesProvider>
  );
}
